import { useCallback, useEffect as useReactEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, SelectOptionGroups, Text } from '@factorialco/deprecated-design-system'
import { F0Text } from '@factorialco/f0-react'
import {
  Input,
  ResourceHeader,
  RichTextEditor,
  resultType,
} from '@factorialco/f0-react/dist/experimental'
import { Calendar, FileFilled } from '@factorialco/f0-react/icons/app'
import {
  CreatePostDocument,
  FeatureFlagAvailabilitiesDocument,
  GroupsWithPostPermissionDocument,
  PostDataFragment,
  SearchEmployeesForMentionsDocument,
  UpdatePostDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, FormProvider, useFormContext } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import SchedulePostModal from 'modules/posts/components/SchedulePost/SchedulePostModal'
import { useCommunitiesClapsFeature } from 'modules/posts/hooks/useCommunitiesClapsFeature'

import Attachments from './Attachments'
import Event from './Event'
import ImageUploader from './ImageUploader'
import PublishModal from './PublishModal'
import { PostFormValues } from './types'

interface InnerProps extends Props {
  openModal: boolean
  setOpenModal: (value: boolean) => void
  setNewAttachments: (attachments: File[]) => void
  attachmentIdsToDelete: number[]
  setAttachmentIdsToDelete: (ids: number[]) => void
  setPublishedAtOverride: (value: string | null | undefined) => void
  groupOptions: SelectOptionGroups
  isEvent?: boolean
}

type SubmitPostMode = 'publish' | 'schedule' | 'draft'

const getPublishedAtForMode = (mode: SubmitPostMode, scheduledDate?: string): string | null => {
  switch (mode) {
    case 'publish':
      return moment().toISOString()
    case 'schedule':
      return scheduledDate ?? moment().toISOString()
    case 'draft':
      return null
    default:
      return moment().toISOString()
  }
}

const ManagePost = ({
  isKudos,
  post,
  openModal,
  setOpenModal,
  setNewAttachments,
  attachmentIdsToDelete,
  setAttachmentIdsToDelete,
  setPublishedAtOverride,
  groupOptions,
  isEvent,
}: InnerProps) => {
  const form = useFormContext<PostFormValues>()
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const hasMentioning = useCommunitiesClapsFeature()
  const { editorConfig, loading: loadingEditorConfig } = useRichTextEditor()
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { data: communitiesFeatureData } = useQuery(FeatureFlagAvailabilitiesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      companyId,
      featureNames: ['dev_communities_page'],
    },
  })

  const isCommunitiesPageEnabled =
    communitiesFeatureData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'dev_communities_page'
    )?.enabled ?? false

  const postGroupId = form.values.postGroupId
  const { setFieldValue } = form

  useReactEffect(() => {
    if (!post && Array.isArray(groupOptions) && groupOptions.length > 0 && postGroupId === 0) {
      const firstId = groupOptions[0]?.value
      if (firstId) {
        setFieldValue('postGroupId', firstId)
      }
    }
  }, [groupOptions, post, postGroupId, setFieldValue])

  const { navigation } = useNavigation()
  const [searchEmployees] = useLazyQuery(SearchEmployeesForMentionsDocument)
  const [initialMentionUsers, setInitialMentionUsers] = useState<
    Array<{ id: number; label: string; image_url: string; href?: string }>
  >([])

  useReactEffect(() => {
    if (!hasMentioning || !companyId) return

    let cancelled = false
    searchEmployees({ variables: { companyId, search: 'a' } }).then((result) => {
      if (cancelled) return
      const nodes = result.data?.employees?.employeeNamesConnection?.nodes
      if (!nodes) return
      setInitialMentionUsers(
        nodes.map((employee) => ({
          id: employee.access.id,
          label: employee.fullName ?? '',
          image_url: employee.avatar?.url ?? '',
          href: navigation.employees.id.fullPath({ id: employee.id }),
        }))
      )
    })
    return () => {
      cancelled = true
    }
  }, [hasMentioning, companyId, navigation, searchEmployees])

  const mentionUsersReady = !hasMentioning || initialMentionUsers.length > 0

  const handleMentionSearch = useCallback(
    async (queryString: string) => {
      try {
        const result = await searchEmployees({
          variables: { companyId, search: queryString.trim() || ' ' },
        })

        const nodes = result.data?.employees?.employeeNamesConnection?.nodes
        if (!nodes) return []

        return nodes.map((employee) => ({
          id: employee.access.id,
          label: employee.fullName ?? '',
          image_url: employee.avatar?.url ?? '',
          href: navigation.employees.id.fullPath({ id: employee.id }),
        }))
      } catch {
        return []
      }
    },
    [companyId, navigation, searchEmployees]
  )

  const onRichTextEditorChange = useCallback(
    (result: resultType) => {
      form.setValues({
        ...form.values,
        description: result.value ?? '',
        mentionedAccessIds: result.mentionIds ?? [],
      })
    },
    [form]
  )

  useEffect(() => {
    form.validateForm()
    // NOTE: validating the form change its state so adding form as
    //       dependency will cause an infinite loop here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClick = () => {
    setOpenModal(true)
    track(trackingEvents.communities.createPostContinue)
  }

  const handleSaveDraft = () => {
    setPublishedAtOverride(getPublishedAtForMode('draft'))
    form.handleSubmit()
  }

  const handleSchedule = (scheduledDate: string) => {
    setPublishedAtOverride(getPublishedAtForMode('schedule', scheduledDate))
    form.handleSubmit()
    setShowScheduleModal(false)
  }

  const headerTitle = post
    ? i18n.t('posts.communities.post.edit')
    : isKudos
      ? i18n.t('posts.kudos.title')
      : isEvent
        ? i18n.t('posts.communities.new_event_post')
        : i18n.t('posts.communities.new_post')

  return (
    <Box width="full" gap="s24" paddingBottom="s24">
      <ResourceHeader
        title={headerTitle}
        primaryAction={{
          label: i18n.t('common.continue'),
          onClick: () => handleClick(),
          disabled: !form.isValid || form.isSubmitting,
        }}
        secondaryActions={
          isCommunitiesPageEnabled
            ? [
                {
                  label: i18n.t('posts.communities.schedule_post.title'),
                  onClick: () => setShowScheduleModal(true),
                  disabled: !form.isValid || form.isSubmitting,
                  variant: 'outline',
                  icon: Calendar,
                },
                {
                  label: i18n.t('posts.communities.quick_post.save_as_draft'),
                  onClick: handleSaveDraft,
                  disabled: !form.isValid || form.isSubmitting,
                  variant: 'outline',
                  icon: FileFilled,
                },
              ]
            : undefined
        }
      />
      <Box alignItems="center">
        <Box gap="s16" width="s600">
          <F0Text
            content={i18n.t('posts.communities.basic_information')}
            variant="body"
            style={{ fontWeight: 600, fontSize: '16px' }}
          />
          <Box gap="s8">
            <F0Text
              style={{ fontWeight: 500 }}
              content={i18n.t('posts.communities.post_cover')}
              variant="description"
            />
            <ImageUploader />
            <Box alignItems="flexStart">
              <Text size="200" color="radicalRed1000" capitalize>
                {form.errors.coverImageUrl}
              </Text>
            </Box>
          </Box>
          <Box aria-label={i18n.t('posts.communities.aria_label_posts_title_field')}>
            <Input
              value={form.values.title}
              label={i18n.t('posts.communities.title')}
              placeholder={i18n.t('posts.communities.title_placeholder')}
              onChange={(title: string) => form.setValues({ ...form.values, title })}
              error={form.values.title !== form.initialValues.title ? form.errors.title : ''}
            />
          </Box>
          <Box aria-label={i18n.t('posts.communities.aria_label_posts_description_field')}>
            <F0Text
              style={{ fontWeight: 500, paddingBottom: '8px' }}
              content={i18n.t('posts.communities.description')}
              variant="description"
            />
            {loadingEditorConfig || !mentionUsersReady ? (
              <RichTextEditor.Skeleton rows={1} />
            ) : (
              <RichTextEditor
                {...editorConfig}
                title={form.values.title}
                placeholder={
                  isKudos
                    ? i18n.t('posts.kudos.description_placeholder')
                    : i18n.t('posts.communities.description_placeholder')
                }
                initialEditorState={{ content: form.values.description }}
                onChange={onRichTextEditorChange}
                filesConfig={{
                  acceptedFileType: ['image', 'video', 'pdf'],
                  multipleFiles: true,
                  onFiles: (attachments) => setNewAttachments(attachments),
                }}
                maxCharacters={10000}
                {...(hasMentioning && {
                  mentionsConfig: {
                    users: initialMentionUsers,
                    onMentionQueryStringChanged: handleMentionSearch,
                  },
                })}
              />
            )}
            <Text size="200" color="radicalRed1000" capitalize>
              {form.values.description !== form.initialValues.description
                ? form.errors.description
                : ''}
            </Text>
            <Box alignItems="flexEnd">
              <F0Text
                content={i18n.t('posts.communities.attachments_size_per_post')}
                variant="description"
              />
            </Box>
          </Box>
          {post && post.savedAttachmentsConnection.edges.length > 0 && (
            <Attachments
              post={post}
              attachmentIdsToDelete={attachmentIdsToDelete}
              setAttachmentIdsToDelete={setAttachmentIdsToDelete}
            />
          )}
          {!post && !isKudos && !isEvent && (
            <>
              <F0Text
                content={i18n.t('posts.communities.post_settings')}
                variant="body"
                style={{ fontWeight: 600, fontSize: '16px' }}
              />
              <Event
                isEvent={form.values.isEvent}
                startsAt={form.values.startsAt}
                location={form.values.location}
                startsAtError={form.errors.startsAt}
                onToggleIsEvent={(checked) => {
                  form.setFieldValue('isEvent', checked)
                  if (checked && !form.values.startsAt) {
                    form.setFieldValue('startsAt', moment().toISOString())
                  }
                }}
                onChangeStartsAt={(nextIso) => form.setFieldValue('startsAt', nextIso)}
                onChangeLocation={(next) => form.setFieldValue('location', next)}
              />
            </>
          )}
        </Box>
        {openModal && (
          <PublishModal
            isSubmitting={form.isSubmitting}
            onSubmit={() => {
              form.handleSubmit()
            }}
            onCloseModal={() => {
              setOpenModal(false)
            }}
            groupOptions={groupOptions}
          />
        )}
        <SchedulePostModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onConfirm={handleSchedule}
        />
      </Box>
    </Box>
  )
}

interface Props {
  post?: PostDataFragment
  isKudos?: boolean
  isEvent?: boolean
}

const ManagePostForm = ({ post, isKudos, isEvent = false }: Props) => {
  const location = useLocation()
  const [openModal, setOpenModal] = useState(false)
  const publishedAtOverrideRef = useRef<string | null | undefined>(undefined)

  const setPublishedAtOverride = useCallback((value: string | null | undefined) => {
    publishedAtOverrideRef.current = value
  }, [])

  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [newAttachments, setNewAttachments] = useState<File[]>([])
  const [attachmentIdsToDelete, setAttachmentIdsToDelete] = useState<number[]>([])

  const { data: groupsData, fetchMore } = useQuery(GroupsWithPostPermissionDocument, {
    variables: {
      first: 100,
      filter: {
        identifier: {
          notEq: 'celebrations',
        },
      },
    },
  })

  const isFetchingMore = useRef(false)

  useReactEffect(() => {
    const connection = groupsData?.posts?.groupsConnection
    const hasNextPage = connection?.pageInfo?.hasNextPage
    const endCursor = connection?.pageInfo?.endCursor

    if (hasNextPage && endCursor && !isFetchingMore.current) {
      isFetchingMore.current = true
      fetchMore({
        variables: {
          after: endCursor,
          first: 100,
          filter: {
            identifier: {
              notEq: 'celebrations',
            },
          },
        },
      }).finally(() => {
        isFetchingMore.current = false
      })
    }
  }, [groupsData, fetchMore])

  const groupOptions: SelectOptionGroups = useMemo(() => {
    const edges = groupsData?.posts?.groupsConnection?.edges
    if (!edges) return []

    return edges
      .map((edge) => edge.node)
      .filter((group) => {
        return group?._policies?.addPosts?.can
      })
      .map((group) => ({
        label: group?.title ?? '',
        value: group?.id ?? 0,
      }))
      .filter((g) => g.value > 0)
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [groupsData])

  const firstGroupId = groupOptions[0]?.value

  const defaultValues: PostFormValues = useMemo(
    () => ({
      title: isKudos
        ? i18n.t('posts.kudos.automatic_title_with_emoji', { emojis: '\u{1F44F}' })
        : '',
      description: '',
      isEvent,
      postGroupId: firstGroupId ?? 0,
      allowCommentsAndReactions: true,
      sendNotifications: false,
      startsAt: new Date().toISOString(),
      location: '',
      coverImageUrl: null,
      mentionedAccessIds: [],
    }),
    [isKudos, isEvent, firstGroupId]
  )

  const [createPost] = useMutation(CreatePostDocument, {
    extractorFn: (data) => data.postsMutations.createPostsPost,
  })

  const [updatePost] = useMutation(UpdatePostDocument, {
    extractorFn: (data) => data.postsMutations.updatePostsPost,
  })

  const entity = {
    ...(post ?? {}),
    isEvent: post?.type === 'Posts::V2::Event' || isEvent,
    postGroupId: post?.group?.id ?? 0,
    allowCommentsAndReactions: post?.allowCommentsAndReactions ?? true,
  }

  return (
    <FormProvider
      entity={entity}
      defaultValues={defaultValues}
      validate={(data) => {
        const errors: FormErrors<PostFormValues> = {}

        if (data.title === '') {
          errors.title = i18n.t('common.validations.empty')
        } else if (/<\/?[a-z][\s\S]*>/i.test(data.title)) {
          errors.title = i18n.t('posts.communities.error_input')
        }

        if (data.description === '') {
          errors.description = i18n.t('common.validations.empty')
        }

        if (Object.keys(errors).length > 0) {
          setOpenModal(false)
        }

        return errors
      }}
      onSubmit={async (data, { setErrors }) => {
        if (!data.postGroupId || data.postGroupId === 0) {
          setErrors({
            postGroupId: i18n.t('common.validations.empty'),
          })
          return { success: false, errors: [] }
        }

        const publishAt =
          publishedAtOverrideRef.current === undefined
            ? moment().toISOString()
            : publishedAtOverrideRef.current
        const type = isKudos ? 'kudo' : data.isEvent ? 'event' : 'announcement'
        const communityType = type === 'announcement' ? 'default' : 'custom'
        let handled

        if (post) {
          const isClearingPublishedAt = publishedAtOverrideRef.current === null
          const result = await updatePost({
            variables: {
              id: post.id,
              description: data.description,
              mentionedAccessIds: data.mentionedAccessIds,
              postGroupId: data.postGroupId,
              title: data.title,
              allowCommentsAndReactions: data.allowCommentsAndReactions,
              sendNotifications: data.sendNotifications,
              location: data.location,
              startsAt: data.startsAt,
              ...(isClearingPublishedAt
                ? { clearPublishedAt: true }
                : publishedAtOverrideRef.current !== undefined
                  ? { publishedAt: publishAt }
                  : {}),
              deleteCoverImage: data.coverImageUrl === null,
              newCoverImage: data.coverImageUrl instanceof File ? data.coverImageUrl : undefined,
              newAttachments,
              deletedAttachments: attachmentIdsToDelete,
            },
          })

          handled = handleResult(result, (errors) => {
            if (errors.newCoverImage) {
              setErrors({
                coverImageUrl: errors.newCoverImage,
              })
            }
            if (errors.newAttachments) {
              setErrors({
                description: errors.newAttachments,
              })
            }
            app.setFlash({
              type: 'wrong',
              message: i18n.t('posts.communities.error_message'),
            })
          })

          if (isKudos) {
            track(trackingEvents.communities.sendClapsEdit, {
              is_succesfull: handled.success,
            })
          } else {
            track(trackingEvents.communities.createPostEdit, {
              community: communityType,
              allow_comm_react: data.allowCommentsAndReactions,
              send_notifications: data.sendNotifications,
            })
          }
        } else {
          const result = await createPost({
            variables: {
              description: data.description,
              mentionedAccessIds: data.mentionedAccessIds,
              postGroupId: data.postGroupId,
              publishedAt: publishAt,
              title: data.title,
              type,
              allowCommentsAndReactions: data.allowCommentsAndReactions,
              sendNotifications: data.sendNotifications,
              location: data.location,
              startsAt: data.startsAt,
              coverImage: data.coverImageUrl,
              attachments: newAttachments,
            },
          })

          handled = handleResult(result, (errors) => {
            if (errors.coverImage) {
              setErrors({
                coverImageUrl: errors.coverImage,
              })
            }
            if (errors.attachments || errors.description) {
              setErrors({
                description: errors.attachments || errors.description,
              })
            }
            app.setFlash({
              type: 'wrong',
              message: i18n.t('posts.communities.error_message'),
            })
          })

          if (isKudos) {
            track(trackingEvents.communities.sendClapsSubmit, {
              is_succesfull: handled.success,
            })
          } else {
            track(trackingEvents.communities.createPostSubmit, {
              community: communityType,
              allow_comm_react: data.allowCommentsAndReactions,
              send_notifications: data.sendNotifications,
            })
          }
        }

        if (handled.success && handled.data.post) {
          const isFromCommunities = location.pathname.includes(navigation.communities.path)
          if (isFromCommunities) {
            navigate(navigation.communities.post.id.fullPath({ id: handled.data.post.id }))
          } else {
            navigate(navigation.dashboard.post.id.fullPath({ id: handled.data.post.id }))
          }
        } else {
          setOpenModal(false)
        }

        publishedAtOverrideRef.current = undefined
        return handled
      }}
    >
      <ManagePost
        isKudos={isKudos}
        isEvent={isEvent}
        post={post}
        openModal={openModal}
        setOpenModal={setOpenModal}
        setNewAttachments={setNewAttachments}
        attachmentIdsToDelete={attachmentIdsToDelete}
        setAttachmentIdsToDelete={setAttachmentIdsToDelete}
        setPublishedAtOverride={setPublishedAtOverride}
        groupOptions={groupOptions}
      />
    </FormProvider>
  )
}

export default ManagePostForm
